import { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  FormControl,
  FormLabel,
  Select,
  Badge,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import emailjs, { init } from "emailjs-com";
import { useHandlers, useSuppliers } from "../hooks/useSuppliers";
import { useForm } from "react-hook-form";
import { useSourcedProducts } from "../hooks/useProduceType";
import { useAxios } from "../hooks/useAxios";
import { format } from "date-fns";
import { useCancelSourcingProduct } from "../hooks/useSourcingCancellation";

init("zBShcYJ-j5eC46MHD");

const CancelSourcingModal = ({ record, isOpen, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const toast = useToast();
  const { mutate, isLoading } = useCancelSourcingProduct();

  const handleConfirm = () => {
    mutate(
      { id: record.id, notes: reason },
      {
        onSuccess: () => {
          emailjs
            .send("supplier_sourcing", "sourcing_cancellation", {
              lpo_number: record.lpo_number,
              name: record.supplier?.display_name,
              produce: record.product_type?.name,
              total: record.total_cost,
              cancellation_reason: reason,
            })
            .then(() =>
              toast({ title: "Email sent successfully.", status: "success" }),
            )
            .catch((err) => {
              console.error("EmailJS cancellation notice failed:", err);
              toast({
                title: "Record cancelled, but finance email failed to send",
                description:
                  "Please notify finance manually for LPO " + record.lpo_number,
                status: "warning",
              });
            });

          toast({ title: "Sourcing record cancelled", status: "success" });
          onDone();
          onClose();
        },
        onError: (err) => {
          toast({
            title: "Unable to cancel",
            description: err?.response?.data?.message,
            status: "error",
          });
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cancel LPO {record?.lpo_number}?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p style={{ marginBottom: 12 }}>
            This will notify finance to stop payment for this LPO. This can't be
            undone.
          </p>
          <FormControl isRequired>
            <FormLabel>Reason</FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being cancelled?"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Back
          </Button>
          <Button
            colorScheme="red"
            isDisabled={!reason.trim()}
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            Confirm Cancellation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const History = () => {
  const [offset, setOffset] = useState("&offset=0");
  const [activePage, setActivePage] = useState(1);
  const [activeRecord, setActiveRecord] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: supplier } = useSuppliers();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm();
  const formFilters = watch("supplier_id");
  const { data: products, isLoading } = useSourcedProducts(
    formFilters ? `&supplier_id=${formFilters}` : ``,
    offset,
  );

  return (
    <Box>
      <FormControl isInvalid={errors?.name?.message}>
        <FormLabel>Supplier Name</FormLabel>
        <Select
          placeholder="Select agent..."
          name="supplier_id"
          focusBorderColor="brand.800"
          {...register("supplier_id", { required: "Supplier is required" })}
        >
          {supplier
            ?.sort((a, b) => a.display_name.localeCompare(b.display_name))
            .map((supplier) => (
              <option key={supplier.id} value={supplier?.id}>
                {supplier?.display_name}
              </option>
            ))}
        </Select>
      </FormControl>
      <TableContainer mt="2">
        <Table size="md" variant="striped">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>LPO NUmber</Th>
              <Th>Produce Type</Th>
              <Th>Quality Score</Th>
              <Th>Net Weight</Th>
              <Th>Cost/Kg</Th>
              <Th>Total</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {products?.returned_resultset?.map((product) => (
              <Tr key={product.id}>
                <Td>
                  {format(new Date(product.created_at), "dd MMM yyyy HH:mm a")}
                </Td>
                <Td>{product.lpo_number}</Td>
                <Td>{product.product_type?.name}</Td>
                <Td>{product.quality_score}</Td>
                <Td>{product.total_net_weight}</Td>
                <Td>{product.cost_per_kg}</Td>
                <Td>{product.total_cost}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      product.status === "cancelled" ? "red" : "green"
                    }
                  >
                    {product.status}
                  </Badge>
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    isDisabled={product.status === "cancelled"}
                    onClick={() => {
                      setActiveRecord(product);
                      onOpen();
                    }}
                  >
                    Cancel
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {activeRecord && (
        <CancelSourcingModal
          record={activeRecord}
          isOpen={isOpen}
          onClose={onClose}
          onDone={() => setActiveRecord(null)}
        />
      )}
    </Box>
  );
};

export default History;
